export interface HttpRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD' | 'OPTIONS'
  headers?: Record<string, string>
  body?: any
  timeout?: number
  credentials?: 'omit' | 'same-origin' | 'include'
  responseType?: 'json' | 'text' | 'blob' | 'arrayBuffer' | 'formData'
}

export interface HttpResponse<T = any> {
  data: T
  status: number
  statusText: string
  headers: Record<string, string>
  config: HttpRequestOptions
}

export interface HttpInterceptor {
  request?: (config: HttpRequestOptions) => HttpRequestOptions | Promise<HttpRequestOptions>
  response?: (response: HttpResponse) => HttpResponse | Promise<HttpResponse>
  error?: (error: any) => any
}

const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
}

const interceptors: HttpInterceptor[] = []

export function addInterceptor(interceptor: HttpInterceptor): void {
  interceptors.push(interceptor)
}

export function removeInterceptor(interceptor: HttpInterceptor): void {
  const index = interceptors.indexOf(interceptor)
  if (index > -1) {
    interceptors.splice(index, 1)
  }
}

async function applyRequestInterceptors(config: HttpRequestOptions): Promise<HttpRequestOptions> {
  let result = config
  for (const interceptor of interceptors) {
    if (interceptor.request) {
      result = await interceptor.request(result)
    }
  }
  return result
}

async function applyResponseInterceptors(response: HttpResponse): Promise<HttpResponse> {
  let result = response
  for (const interceptor of interceptors) {
    if (interceptor.response) {
      result = await interceptor.response(result)
    }
  }
  return result
}

export async function httpRequest<T = any>(
  url: string,
  options: HttpRequestOptions = {}
): Promise<HttpResponse<T>> {
  const {
    method = 'GET',
    headers = {},
    body,
    timeout = 30000,
    credentials = 'same-origin',
    responseType = 'json',
  } = await applyRequestInterceptors(options)

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await fetch(url, {
      method,
      headers: { ...defaultHeaders, ...headers },
      body: method !== 'GET' && method !== 'HEAD' ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
      credentials,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    let data: any
    switch (responseType) {
      case 'json':
        data = await response.json()
        break
      case 'text':
        data = await response.text()
        break
      case 'blob':
        data = await response.blob()
        break
      case 'arrayBuffer':
        data = await response.arrayBuffer()
        break
      case 'formData':
        data = await response.formData()
        break
    }

    const responseHeaders: Record<string, string> = {}
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value
    })

    const httpResponse: HttpResponse<T> = {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
      config: options,
    }

    return applyResponseInterceptors(httpResponse)
  } catch (error) {
    clearTimeout(timeoutId)
    for (const interceptor of interceptors) {
      if (interceptor.error) {
        interceptor.error(error)
      }
    }
    throw error
  }
}

export function httpGet<T = any>(url: string, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
  return httpRequest<T>(url, { ...options, method: 'GET' })
}

export function httpPost<T = any>(url: string, body?: any, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
  return httpRequest<T>(url, { ...options, method: 'POST', body })
}

export function httpPut<T = any>(url: string, body?: any, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
  return httpRequest<T>(url, { ...options, method: 'PUT', body })
}

export function httpDelete<T = any>(url: string, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
  return httpRequest<T>(url, { ...options, method: 'DELETE' })
}

export function httpPatch<T = any>(url: string, body?: any, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
  return httpRequest<T>(url, { ...options, method: 'PATCH', body })
}

export function setBaseURL(_url: string): void {
  addInterceptor({
    request: (config) => {
      return config
    },
  })
}

export function setAuthToken(token: string): void {
  addInterceptor({
    request: (config) => {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      }
      return config
    },
  })
}
